//==============================================================================
// A generic container that allows arbitrary content to be added to the header
// and body of a modal.
//
// The modal watches for clicks and can be triggered in two fashions:
//   1) An <a> tag with a class that exactly matches the configured trigger
//   2) An <a> tag with an href that matches # + the configured trigger
//
// The first one is the preferred method, but links generated by modules usually
// can't be assigned specific classes in Site Builder.
//==============================================================================
import * as React from 'react';
import classnames from 'classnames';
import { observable } from 'mobx';
import { observer } from 'mobx-react';

import { Heading, Modal, ModalBody, ModalHeader, ModalFooter, Button } from '@msdyn365-commerce-modules/utilities';

import { clsHelper } from '../../utilities/class-name-helper';

import { IModalContainerData } from './modal-container.data';
import { IModalContainerProps } from './modal-container.props.autogenerated';

//==============================================================================
//==============================================================================
const BASE_CLASS = 'modal';
const cls = (fragment?: string) => clsHelper(BASE_CLASS, fragment);

//==============================================================================
//==============================================================================
@observer
class ModalContainer extends React.Component<IModalContainerProps<IModalContainerData>> {

    @observable private isModalOpen: boolean = false;

    //----------------------------------------------------------
    //----------------------------------------------------------
    constructor(props: IModalContainerProps<IModalContainerData>) {
        super(props);

        this.toggleModal = this.toggleModal.bind(this);
        this.clickHandler = this.clickHandler.bind(this);
    }

    //----------------------------------------------------------
    //----------------------------------------------------------
    public clickHandler(event: MouseEvent) {

        // Check to see if an <a> tag was clicked (directly, or a descendant)
        const target = this.findLink(event.target as HTMLElement);

        if (target) {

            // Is the trigger in the class name of the clicked element?
            const isClassMatch = target.classList.contains(this.props.config.trigger!);

            // Is the clicked element a link with the href set to #trigger?
            const isLinkMatch = (target.nodeName === 'A') && (target.getAttribute('href') === `#${this.props.config.trigger}`);

            // If either is true, open the modal
            if (isClassMatch || isLinkMatch) {
                this.openModal();
            }
        }
    }

    //----------------------------------------------------------
    // Find the nearest <a> ancestor
    //----------------------------------------------------------
    private findLink(node: HTMLElement) {

        // Walk up the tree until a link is found (success), we find a <body> (crime scene), or we run out of parents (orphanage)
        while (node && node.nodeName !== 'A') {

            // If we find a body, we're out of here -- technically not needed since we're likely to run out of parents in 3 more steps
            if (node.nodeName === 'BODY') {
                return null;
            }

            // Try again with the parent
            node = node.parentNode as HTMLElement;
        }

        return node;
    }

    //----------------------------------------------------------
    //----------------------------------------------------------
    public componentDidMount(): void {
        if (this.props.config.trigger) {
            document.addEventListener('click', this.clickHandler);
        }
    }

    //----------------------------------------------------------
    //----------------------------------------------------------
    public componentWillUnmount(): void {
        if (this.props.config.trigger) {
            document.removeEventListener('click', this.clickHandler);
        }
    }

    //----------------------------------------------------------
    //----------------------------------------------------------
    public render(): JSX.Element | null {
        const { config, slots } = this.props;

        return (
            <Modal className={config.className} isOpen={this.isModalOpen} toggle={this.toggleModal}>
                <ModalHeader className={cls('header')} toggle={this.toggleModal}>
                    {config.heading && <Heading className={cls('heading')} headingTag={config.heading.tag} text={config.heading.text} />}
                </ModalHeader>

                <ModalBody className={cls('body')}>
                    {slots.bodyContent}
                </ModalBody>

                {config.showFooter && (
                    <ModalFooter className={cls('footer')}>
                        <Button
                            className={classnames(cls('button'), 'close')}
                            onClick={this.toggleModal}
                        >
                            {this.props.resources.modalContainer_closeButton}
                        </Button>
                    </ModalFooter>
                )}
            </Modal>
        );
    }

    //----------------------------------------------------------
    //----------------------------------------------------------
    private openModal() {
        this.isModalOpen = true;
    }

    //----------------------------------------------------------
    //----------------------------------------------------------
    private toggleModal() {
        const elements = document.getElementsByClassName('ms-iframe');
        for (let i = 0; i < elements.length; i++) {
            const iframeParent = elements[i] as HTMLIFrameElement;
            const iframe = iframeParent.getElementsByTagName('iframe')[0];

            if (iframe instanceof HTMLIFrameElement) {
                iframe.src = iframe.src; // When opening the modal, set the src back to its original value for each iframe
            }
        }
        // Toggle the modal open state
        this.isModalOpen = !this.isModalOpen;
    }
}
export default ModalContainer;
